import React from "react";
import { formatDate } from "../../../../../helper/utils";
import "./style.css";
import { getOrderItemPricingMode } from "../../../../../utils/orderPricingMode";

export default function OrderInfo({ data }) {
  // Calculate totals with shipping
  const shippingCharges = Number(data?.totalShippingCharges) || 0;

  // Debug: Check if customerNote exists in data
  // Uncomment to debug: console.log('Order data:', { customerNote: data?.customerNote, allKeys: Object.keys(data || {}) });

  // Calculate importer price - check for wholesale breakdown per product (same as Order Summary)
  // Build a map of productId -> wholesale FBP
  const wholesaleBreakdownMap = new Map();
  if (Array.isArray(data?.breakdown)) {
    for (const breakdownItem of data.breakdown) {
      if (breakdownItem?.pricingMode === 'wholesale' && breakdownItem?.productId && breakdownItem?.breakdown?.[0]?.FBP) {
        wholesaleBreakdownMap.set(breakdownItem.productId.toString(), Number(breakdownItem.breakdown[0].FBP) || 0);
      }
    }
  }

  // Check if this is a manual order (marketplace === 'manual' or createdVia === 'manual_admin' or split from admin)
  const isManualOrder = data?.marketplace?.toLowerCase() === 'manual' || data?.createdVia === 'manual_admin' || data?.createdVia === 'split_admin';

  // Check order type (only for manual orders)
  const orderType = isManualOrder ? (data?.orderType?.toLowerCase()) : null;
  const isOrderWholesale = isManualOrder && orderType === 'wholesale';

  // Helper function to determine pricing mode for an item
  const getPricingMode = (detail) =>
    getOrderItemPricingMode(detail, {
      orderBreakdown: data?.breakdown || [],
      orderType,
      isManualOrder,
      dropshippingLabel: "dropshipping",
    });

  // Helper function to get price for dropshipping item - prioritize finalPrice over breakdown.FBP
  const getDropshippingPrice = (detail) => {
    const variant = detail?.variant;
    // Priority: consumerPrice > finalPrice > breakdown.FBP
    if (variant?.consumerPrice != null && Number(variant.consumerPrice) > 0) {
      return Number(variant.consumerPrice);
    } else if (variant?.finalPrice != null && Number(variant.finalPrice) > 0) {
      return Number(variant.finalPrice);
    }
    const breakdown = variant?.breakdown;
    return Number(breakdown?.FBP ?? breakdown?.fbp) || 0;
  };

  // Helper function to get price for wholesale item - prioritize wholesaleFinalPrice over breakdown
  const getWholesalePrice = (detail) => {
    const variant = detail?.variant;
    // Priority: wholesaleFinalPrice > breakdown FBP
    if (variant?.consumerPrice != null && Number(variant.consumerPrice) > 0) {
      return Number(variant.consumerPrice);
    } else if (variant?.wholesaleFinalPrice != null && Number(variant.wholesaleFinalPrice) > 0) {
      return Number(variant.wholesaleFinalPrice);
    }
    const productId = detail?.product?._id?.toString() || detail?.product?.toString() || detail?.productId?._id?.toString() || (detail?.productId && typeof detail.productId === 'object' ? String(detail.productId._id || detail.productId) : detail?.productId?.toString()) || '';
    const wholesaleFBP = wholesaleBreakdownMap.get(productId);
    if (wholesaleFBP) {
      return wholesaleFBP;
    }
    const breakdown = variant?.breakdown;
    return Number(breakdown?.FBP ?? breakdown?.fbp) || 0;
  };

  // Calculate product total - separate dropshipping and wholesale, then sum
  let productTotal = 0;
  let dropshippingTotal = 0;
  let wholesaleTotal = 0;

  // Group orderDetails by product to calculate wholesale totals per product
  const productGroups = new Map();
  (data?.orderDetails || []).forEach(detail => {
    const productId = detail?.product?._id?.toString() || detail?.product?.toString() || detail?.productId?._id?.toString() || (detail?.productId && typeof detail.productId === 'object' ? String(detail.productId._id || detail.productId) : detail?.productId?.toString()) || '';
    if (!productGroups.has(productId)) {
      productGroups.set(productId, []);
    }
    productGroups.get(productId).push(detail);
  });

  // Calculate totals for each product group
  productGroups.forEach((details, productId) => {
    let productDropshippingTotal = 0;
    let productWholesaleQuantity = 0;
    let productWholesaleTotal = 0;

    // Calculate dropshipping and wholesale separately for this product
    details.forEach(detail => {
      const pricingMode = getPricingMode(detail);
      const quantity = Number(detail?.quantity) || 0;

      if (pricingMode === 'dropshipping') {
        const price = getDropshippingPrice(detail);
        productDropshippingTotal += price * quantity;
      } else {
        // For wholesale, calculate total from individual items if manual order type is wholesale
        if (isManualOrder && isOrderWholesale) {
          const price = getWholesalePrice(detail);
          productWholesaleTotal += price * quantity;
        }
        productWholesaleQuantity += quantity;
      }
    });

    // Add dropshipping total
    dropshippingTotal += productDropshippingTotal;

    // Add wholesale total
    if (isManualOrder && isOrderWholesale && productWholesaleTotal > 0) {
      // Use calculated total from individual items for manual wholesale orders
      wholesaleTotal += productWholesaleTotal;
    } else {
      // Use wholesale breakdown FBP if available (for regular orders)
      const wholesaleFBP = wholesaleBreakdownMap.get(productId);
      if (wholesaleFBP && productWholesaleQuantity > 0) {
        wholesaleTotal += wholesaleFBP;
      }
    }
  });

  // Product total = dropshipping + wholesale
  productTotal = dropshippingTotal + wholesaleTotal;

  const importerPrice = productTotal;
  const imPointsDiscount = Number(data?.imPointsDiscount) || 0;
  const totalWithShipping = importerPrice + shippingCharges;
  const finalAmount = totalWithShipping - imPointsDiscount; // This is what's paid via cash/gateway

  const getPaymentSource = (order) => {
    if (order?.razorpayPaymentId || order?.razorpayOrderId) return "Razorpay";
    if (order?.ccavenueOrderId || order?.ccavenueTrackingId) return "CCAvenue";
    if (order?.bankTransferTxnId || order?.paymentProofUrl) return "Bank Transfer";
    if (order?.transactionId) return "IM Cash";
    return "—";
  };

  const formatCurrencyLocal = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) {
      return "₹0.00";
    }
    return `₹${amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Payment Breakdown Logic
  const pb = data?.paymentBreakdown;
  const hasNewBreakdown = pb && typeof pb === 'object';

  const imPointsAmount = hasNewBreakdown ? (Number(pb.imPoints?.amount) || 0) : imPointsDiscount;
  const walletAmount = hasNewBreakdown ? (Number(pb.wallet?.amount) || 0) : 0;
  const razorpayAmount = hasNewBreakdown ? (Number(pb.razorpay?.amount) || 0) : 0;
  const ccavenueAmount = hasNewBreakdown ? (Number(pb.ccavenue?.amount) || 0) : 0;
  const bankTransferAmount = hasNewBreakdown ? (Number(pb.bankTransfer?.amount) || 0) : 0;

  const hasWallet = hasNewBreakdown ? walletAmount > 0 : (data?.transactionId && !(data?.razorpayPaymentId || data?.razorpayOrderId));
  const hasRazorpay = hasNewBreakdown ? razorpayAmount > 0 : !!(data?.razorpayPaymentId || data?.razorpayOrderId);
  const hasBankTransfer = hasNewBreakdown ? bankTransferAmount > 0 : !!(data?.bankTransferTxnId || data?.paymentProofUrl);
  const hasCCAvenue = hasNewBreakdown ? ccavenueAmount > 0 : !!(data?.ccavenueOrderId || data?.ccavenueTrackingId);
  const paymentSource = getPaymentSource(data);

  const breakdownSum = (imPointsAmount + walletAmount + razorpayAmount + ccavenueAmount + bankTransferAmount);

  const totalOrderValue = data?.grossOrderAmount || (hasNewBreakdown && breakdownSum > 0 ? breakdownSum : totalWithShipping);

  const displaySubtotal = Math.max(0, totalOrderValue - shippingCharges);


  return (
    <div className="order_detail_info_box">
      <div className="order_detail_head">
        <i className="fas fa-receipt" style={{ marginRight: '8px', color: '#3b82f6' }}></i>
        Orders Information
      </div>

      <div className="order_detail_info_row">
        {/* Card 1: Order Details */}
        <div className="order_detail_card">
          <div className="card_header">
            <i className="fas fa-box"></i>
            <span>Order Details</span>
          </div>
          <div className="card_body">
            <div className="card_item">
              <span className="card_label">
                <i className="fas fa-hashtag"></i>
                Importerr Order ID
              </span>
              <span className="card_value order_id_badge">{data?.upsaleoOrderId}</span>
            </div>
            <div className="card_item">
              <span className="card_label">
                <i className="fas fa-shopping-cart"></i>
                Marketplace Order ID
              </span>
              <span className="card_value order_id_badge">{data?.orderId}</span>
            </div>
            <div className="card_item">
              <span className="card_label">
                <i className="fas fa-barcode"></i>
                Order Item ID
              </span>
              <span className="card_value order_id_badge">
                {data?.orderDetails?.map(detail => detail?.orderItemId).filter(Boolean).join(', ') || '-'}
              </span>
            </div>
            <div className="card_item">
              <span className="card_label">
                <i className="fas fa-store"></i>
                Marketplace
              </span>
              <span className="card_value marketplace_badge">
                {data?.marketplace?.toLowerCase() === "manual" ? "MANUAL" : (data?.marketplace || "-")}
              </span>
            </div>
          </div>
        </div>

        {/* Card 2: Status & Tracking */}
        <div className="order_detail_card">
          <div className="card_header">
            <i className="fas fa-signal"></i>
            <span>Status & Tracking</span>
          </div>
          <div className="card_body">
            <div className="card_item">
              <span className="card_label">
                <i className="fas fa-calendar-alt"></i>
                Order Date
              </span>
              <span className="card_value">{formatDate(data.createdAt)}</span>
            </div>
            <div className="card_item">
              <span className="card_label">
                <i className="fas fa-info-circle"></i>
                Order Status
              </span>
              <span className={`card_value status_badge status_${data.status?.toLowerCase()}`}>
                {data.status}
                {data?.fromFailedOrder && (
                  <span className="failed_order_badge_inline">
                    <i className="fas fa-exchange-alt"></i>
                    From Failed Order
                  </span>
                )}
              </span>
            </div>
            {data?.fromFailedOrder && data?.asinMappedAt && (
              <div className="card_item">
                <span className="card_label">
                  <i className="fas fa-check-circle"></i>
                  ASIN Mapped
                </span>
                <span className="card_value">{formatDate(data.asinMappedAt)}</span>
              </div>
            )}
            <div className="card_item">
              <span className="card_label">
                <i className="fas fa-shipping-fast"></i>
                Shipping Method
              </span>
              <span className="card_value">
                {data?.trackingShippingMethod || "-"}
              </span>
            </div>
            <div className="card_item">
              <span className="card_label">
                <i className="fas fa-barcode"></i>
                Tracking Number
              </span>
              <span className="card_value tracking_number">
                {data?.trackingId || "-"}
              </span>
            </div>
          </div>
        </div>

        {/* Card 3: Recipient Information */}
        <div className="order_detail_card">
          <div className="card_header">
            <i className="fas fa-user-circle"></i>
            <span>Recipient Information</span>
          </div>
          <div className="card_body">
            {data?.shippingMethod === 'self-pickup' ? (
              <>
                {data?.warehouseName ? (
                  <>
                    <div className="card_item">
                      <span className="card_label">
                        <i className="fas fa-warehouse"></i>
                        Pickup Warehouse
                      </span>
                      <span className="card_value">{data.warehouseName}</span>
                    </div>
                    <div className="card_item">
                      <span className="card_label">
                        <i className="fas fa-map-marker-alt"></i>
                        Warehouse Address
                      </span>
                      <span className="card_value">
                        {[
                          data?.warehouseAddress1,
                          data?.warehouseAddress2,
                          data?.warehouseCity,
                          data?.warehouseState,
                          data?.warehousePincode
                        ]
                          .filter(Boolean)
                          .join(", ") || "-"}
                      </span>
                    </div>
                    {data?.warehousePhone && (
                      <div className="card_item">
                        <span className="card_label">
                          <i className="fas fa-phone"></i>
                          Warehouse Phone
                        </span>
                        <span className="card_value">{data.warehousePhone}</span>
                      </div>
                    )}
                    {data?.warehouseLandmark && (
                      <div className="card_item">
                        <span className="card_label">
                          <i className="fas fa-landmark"></i>
                          Landmark
                        </span>
                        <span className="card_value">{data.warehouseLandmark}</span>
                      </div>
                    )}
                    {data?.warehouseInstructions && (
                      <div className="card_item">
                        <span className="card_label">
                          <i className="fas fa-info-circle"></i>
                          Pickup Instructions
                        </span>
                        <span className="card_value warning_badge_small">
                          {data.warehouseInstructions}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="card_item">
                    <span className="card_label">
                      <i className="fas fa-warehouse"></i>
                      Pickup Warehouse
                    </span>
                    <span className="card_value error_badge_small">Warehouse details not available</span>
                  </div>
                )}
                <div className="card_item">
                  <span className="card_label">
                    <i className="fas fa-shipping-fast"></i>
                    Method
                  </span>
                  <span className="card_value success_badge_small">
                    <i className="fas fa-hand-holding"></i>
                    Self Pickup
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="card_item">
                  <span className="card_label">
                    <i className="fas fa-user"></i>
                    Recipient Name
                  </span>
                  <span className="card_value">{data?.recipientName || "-"}</span>
                </div>
                <div className="card_item">
                  <span className="card_label">
                    <i className="fas fa-phone"></i>
                    Telephone
                  </span>
                  <span className="card_value">{data?.shippingPhoneNumber || "-"}</span>
                </div>
                <div className="card_item">
                  <span className="card_label">
                    <i className="fas fa-map-marker-alt"></i>
                    Shipping Address
                  </span>
                  <span className="card_value">
                    {[data?.shippingAddress1,
                    data?.shippingAddress2,
                    data?.shippingAddress3,
                    data?.shippingCity,
                    data?.shippingState,
                    data?.shippingPostalCode]
                      .filter(Boolean)
                      .join(", ") || "-"}
                  </span>
                </div>
              </>
            )}
            {[data?.billingAddress1,
            data?.billingAddress2,
            data?.billingAddress3,
            data?.billingCity,
            data?.billingState,
            data?.billingPostalCode].some(Boolean) && (
                <div className="card_item">
                  <span className="card_label">
                    <i className="fas fa-file-invoice"></i>
                    Billing Address
                  </span>
                  <span className="card_value">
                    {[data?.billingAddress1,
                    data?.billingAddress2,
                    data?.billingAddress3,
                    data?.billingCity,
                    data?.billingState,
                    data?.billingPostalCode]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </div>
              )}
          </div>
        </div>

        {/* Card 4: Order Summary */}
        <div className="order_detail_card price_summary_card">
          <div className="card_header" style={{ background: 'linear-gradient(to right, #f8fafc, #f1f5f9)' }}>
            <i className="fas fa-receipt" style={{ color: '#475569' }}></i>
            <span style={{ color: '#1e293b', fontWeight: '700' }}>Order Summary</span>
          </div>
          <div className="card_body" style={{ padding: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Subtotal & Shipping */}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#475569', fontWeight: '500' }}>
                <span>Subtotal ({data?.orderDetails?.length || 0} items)</span>
                <span>{formatCurrencyLocal(displaySubtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#475569', fontWeight: '500' }}>
                <span>Shipping Charges</span>
                <span>{formatCurrencyLocal(shippingCharges)}</span>
              </div>

              {/* Divider if breakdown exists */}
              {(imPointsAmount > 0 || hasWallet || hasRazorpay || hasCCAvenue || hasBankTransfer) && (
                <div style={{ borderTop: '1px dashed #e2e8f0', margin: '4px 0' }}></div>
              )}

              {/* Payment Breakdown items */}
              {imPointsAmount > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: '#475569', fontSize: '13px', fontWeight: '500' }}>IM Points</span>
                  <span style={{ color: '#059669', fontWeight: '600', fontSize: '13px' }}>{formatCurrencyLocal(imPointsAmount)}</span>
                </div>
              )}

              {hasWallet && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: '#475569', fontSize: '13px', fontWeight: '500' }}>IM Cash</span>
                  <span style={{ color: '#7c3aed', fontWeight: '600', fontSize: '13px' }}>{formatCurrencyLocal(hasNewBreakdown ? walletAmount : finalAmount)}</span>
                </div>
              )}

              {hasRazorpay && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: '#475569', fontSize: '13px', fontWeight: '500' }}>Razorpay</span>
                  <span style={{ color: '#2563eb', fontWeight: '600', fontSize: '13px' }}>{formatCurrencyLocal(hasNewBreakdown ? razorpayAmount : finalAmount)}</span>
                </div>
              )}

              {hasCCAvenue && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: '#475569', fontSize: '13px', fontWeight: '500' }}>CCAvenue</span>
                  <span style={{ color: '#dc2626', fontWeight: '600', fontSize: '13px' }}>{formatCurrencyLocal(hasNewBreakdown ? ccavenueAmount : finalAmount)}</span>
                </div>
              )}

              {hasBankTransfer && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: '#475569', fontSize: '13px', fontWeight: '500' }}>Bank Transfer</span>
                  <span style={{ color: '#b45309', fontWeight: '600', fontSize: '13px' }}>{formatCurrencyLocal(hasNewBreakdown ? bankTransferAmount : finalAmount)}</span>
                </div>
              )}
            </div>

            {/* Grand Total Green Box */}
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#f0fdf4',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              border: '1px solid #dcfce7'
            }}>
              <span style={{ fontWeight: '700', color: '#166534', fontSize: '14px' }}>Grand Total</span>
              <span style={{ fontWeight: '800', color: '#166534', fontSize: '18px' }}>{formatCurrencyLocal(totalOrderValue)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Note Section - Prominent Display */}
      {(() => {
        // Check multiple possible field names and be more lenient
        const customerNote = data?.customerNote || data?.note || data?.customer_note;
        // Check if note exists (even if it's just whitespace, we'll trim it)
        const noteValue = customerNote ? String(customerNote).trim() : '';
        const hasNote = noteValue.length > 0;

        if (!hasNote) return null;

        return (
          <div style={{
            marginTop: '20px',
            padding: '16px',
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            border: '2px solid #3dd0e5',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(61, 208, 229, 0.15)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px'
            }}>
              <i className="fas fa-sticky-note" style={{
                color: '#3dd0e5',
                fontSize: '1.125rem'
              }}></i>
              <strong style={{
                fontSize: '0.875rem',
                color: '#0c4a6e',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Customer Note
              </strong>
            </div>
            <div style={{
              padding: '12px',
              background: '#ffffff',
              border: '1px solid #bae6fd',
              borderRadius: '6px',
              color: '#0c4a6e',
              fontSize: '0.875rem',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              minHeight: '50px'
            }}>
              {noteValue}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
